import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { RootState } from '../../store';
import {
  useGetSchoolClassesQuery,
  useGetSchoolSectionsMutation,
  useLazyGetSchoolMediumQuery,
  useLazyGetStudentNamesQuery,
  usePromoteStudentsMutation,
  type SectionItem,
  type StudentItem,
} from './StudentPromotion.service';
import './student-promotion.scss';

interface Option {
  label: string;
  value: number | string;
}

// A student row plus the local "checked for promotion" flag.
type StudentRow = StudentItem & { check: boolean };

// class_id → display label used for the "To" class when it's a single next class.
const TARGET_LABELS: Record<number, string> = {
  1: 'I',
  2: 'II',
  3: 'III',
  4: 'IV',
  5: 'V',
  6: 'VI',
  7: 'VII',
  8: 'VIII',
  9: 'IX',
  10: 'X',
  11: 'XI',
  12: 'XII',
  13: 'LKG',
  14: 'UKG',
  15: 'PRE-KG',
};

// Mirrors Angular: cls 15 → 13, cls 14 → 1, otherwise the next class.
const computeTargetValue = (cls: number): number =>
  cls === 15 ? 13 : cls === 14 ? 1 : cls + 1;

const StudentPromotion: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const schoolId = s.schoolId;
  const highClass = s.highClass;
  const schoolTypeId = s.schoolTypeId;

  const toast = useRef<Toast>(null);

  // ── "From" side ────────────────────────────────────────────────────────────
  const [fromClass, setFromClass] = useState<number | ''>('');
  const [fromSection, setFromSection] = useState<string>('');
  const [fromMediumName, setFromMediumName] = useState<string>('');
  const [fromGroupName, setFromGroupName] = useState<string>('');

  // ── "To" (target) side ───────────────────────────────────────────────────────
  const [toClass, setToClass] = useState<number | ''>('');
  const [toSection, setToSection] = useState<string>('');
  const [toMediumName, setToMediumName] = useState<string>('');
  const [toGroupName, setToGroupName] = useState<string>('');

  // Derived target class/medium data needed to build the promote payload.
  const [targetValue, setTargetValue] = useState<number | ''>('');
  const [targetMedium, setTargetMedium] = useState<number | string>('');
  const [targetGroupId, setTargetGroupId] = useState<number | string>('');

  // ── dropdown option lists ────────────────────────────────────────────────────
  const [classDataTarget, setClassDataTarget] = useState<Option[]>([]);
  const [sectionData, setSectionData] = useState<Option[]>([]);
  const [sectionDataTarget, setSectionDataTarget] = useState<Option[]>([]);

  // ── student table ─────────────────────────────────────────────────────────────
  const [studentList, setStudentList] = useState<StudentRow[]>([]);
  const [studentAllCheck, setStudentAllCheck] = useState<boolean>(false);
  const [promoteView, setPromoteView] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState<string>('');

  const [submitted, setSubmitted] = useState<boolean>(false);

  // Academic year: from March onward it's the current→next year, else prev→current.
  const acYear = useMemo(() => {
    const now = new Date();
    const yr = now.getFullYear();
    const month = now.getMonth() + 1;
    return month >= 3 ? `${yr}-${yr + 1}` : `${yr - 1}-${yr}`;
  }, []);

  // ── API hooks ─────────────────────────────────────────────────────────────────
  const { data: classesResp } = useGetSchoolClassesQuery(
    { schoolId },
    { skip: !s.loaded || !schoolId }
  );
  const [getSchoolSections] = useGetSchoolSectionsMutation();
  const [getSchoolMedium] = useLazyGetSchoolMediumQuery();
  const [getStudentNames] = useLazyGetStudentNamesQuery();
  const [promoteStudents] = usePromoteStudentsMutation();

  // Class dropdown options, derived from the fetched class list.
  const classData = useMemo<Option[]>(
    () =>
      (classesResp?.result ?? []).map((c) => ({
        label: c.class_studying,
        value: c.class_id,
      })),
    [classesResp]
  );

  const notify = (
    severity: 'success' | 'error' | 'warn',
    detail: string
  ): void => {
    toast.current?.show({ severity, detail, life: 4000 });
  };

  // Clears everything below the "From" class when it changes / resets.
  const resetBelowFromClass = (): void => {
    setFromSection('');
    setFromMediumName('');
    setFromGroupName('');
    setToClass('');
    setToSection('');
    setToMediumName('');
    setToGroupName('');
    setTargetMedium('');
    setTargetGroupId('');
    setSectionData([]);
    setSectionDataTarget([]);
  };

  // ── "From" class change ─────────────────────────────────────────────────────
  const handleFromClassChange = async (cls: number): Promise<void> => {
    setPromoteView(false);
    setStudentList([]);
    setStudentAllCheck(false);
    setFromClass(cls);

    const tv = computeTargetValue(cls);
    setTargetValue(tv);

    // Build the "To" class options.
    let targets: Option[] = [];
    if (cls !== highClass) {
      if (schoolTypeId === 3) {
        // Pre-primary schools: allow promoting to any higher existing class,
        // plus the special LKG/UKG entries for PRE-KG/LKG/UKG sources.
        let floor = cls;
        if (cls === 13) {
          floor = 0;
          targets.push({ label: 'UKG', value: 14 });
        } else if (cls === 14) {
          floor = 0;
        } else if (cls === 15) {
          floor = 0;
          targets.push({ label: 'LKG', value: 13 });
          targets.push({ label: 'UKG', value: 14 });
        }
        classData.forEach((el) => {
          const v = Number(el.value);
          if (v > floor && v <= highClass) targets.push(el);
        });
      } else {
        targets = [{ label: TARGET_LABELS[tv] ?? '', value: tv }];
      }
    }
    setClassDataTarget(targets);

    resetBelowFromClass();

    // Fetch the sections for the selected "From" class.
    try {
      const res = await getSchoolSections({
        records: { class_id: cls, school_id: schoolId },
      }).unwrap();
      setSectionData(
        (res.result ?? []).map((i: SectionItem) => ({
          label: i.section,
          value: i.section,
        }))
      );
    } catch {
      setSectionData([]);
    }
  };

  // ── "From" section change ───────────────────────────────────────────────────
  const handleFromSectionChange = async (section: string): Promise<void> => {
    setPromoteView(false);
    setStudentList([]);
    setFromSection(section);

    // Reset the target side — it depends on the source selection.
    setToClass('');
    setToSection('');
    setToMediumName('');
    setToGroupName('');

    if (typeof fromClass !== 'number') return;
    try {
      const res = await getSchoolMedium({
        class_id: fromClass,
        section,
        school_id: schoolId,
      }).unwrap();
      const dm = res.result ?? [];
      if (dm.length) {
        setFromMediumName(dm[0].medium_name ?? '');
        setFromGroupName(dm[0].group_name ?? '');
      }
    } catch {
      /* medium lookup failed — leave fields blank */
    }
  };

  // ── Get Student List ────────────────────────────────────────────────────────
  const handleGetStudentList = async (): Promise<void> => {
    setStudentAllCheck(false);
    console.log(fromClass, typeof fromClass, 'fromClass');
    console.log(fromSection, 'fromSection');
    if (!fromClass || !fromSection) {
      notify('warn', 'Please select the From class and section');
      return;
    }
    try {
      const res = await getStudentNames({
        class_studying_id: fromClass,
        class_section: fromSection,
        school_id: schoolId,
      }).unwrap();
      const list = res.result ?? [];
      if (list.length > 0) {
        setStudentList(list.map((student) => ({ ...student, check: false })));
        setPromoteView(false);
      } else {
        setStudentList([]);
        notify('warn', 'Data not Found');
      }
    } catch {
      setStudentList([]);
      notify('warn', 'Data not Found');
    }
  };

  // ── "To" class change ──────────────────────────────────────────────────────
  const handleToClassChange = async (cls: number): Promise<void> => {
    setToClass(cls);
    setToSection('');
    setToMediumName('');
    setToGroupName('');
    // Sections for the target are looked up using the computed target value
    // (mirrors Angular's class_record_target using targ_val).
    // if (typeof targetValue !== 'number') return;
    try {
      const res = await getSchoolSections({
        records: { class_id: targetValue, school_id: schoolId },
      }).unwrap();
      setSectionDataTarget(
        (res.result ?? []).map((i: SectionItem) => ({
          label: i.section,
          value: i.section,
        }))
      );
    } catch {
      setSectionDataTarget([]);
    }
  };

  // ── "To" section change ─────────────────────────────────────────────────────
  const handleToSectionChange = async (section: string): Promise<void> => {
    setToSection(section);
    // if (typeof toClass !== 'number') return;
    try {
      const res = await getSchoolMedium({
        class_id: toClass,
        section,
        school_id: schoolId,
      }).unwrap();
      const dm = res.result ?? [];
      if (dm.length) {
        setTargetMedium(dm[0].school_medium_id);
        setTargetGroupId(dm[0].group_id);
        setToMediumName(dm[0].medium_name ?? '');
        setToGroupName(dm[0].group_name ?? '');
      }
    } catch {
      /* medium lookup failed — leave fields blank */
    }
  };

  // ── student selection ───────────────────────────────────────────────────────
  const handleSelectAll = (): void => {
    const allChecked =
      studentList.length > 0 && studentList.every((i) => i.check);
    const next = !allChecked;
    setStudentList(studentList.map((i) => ({ ...i, check: next })));
    setStudentAllCheck(next);
    setPromoteView(next);
  };

  const handleRowCheck = (rowId: number | string, checked: boolean): void => {
    const updated = studentList.map((i) =>
      i.id === rowId ? { ...i, check: checked } : i
    );
    setStudentList(updated);
    setPromoteView(updated.some((i) => i.check));
    setStudentAllCheck(updated.length > 0 && updated.every((i) => i.check));
  };

  // ── Promote ─────────────────────────────────────────────────────────────────
  const resetAfterPromote = (): void => {
    setFromClass('');
    setClassDataTarget([]);
    resetBelowFromClass();
    setStudentList([]);
    setStudentAllCheck(false);
    setPromoteView(false);
    setSubmitted(false);
    setGlobalFilter('');
  };

  const handlePromote = (): void => {
    setSubmitted(true);

    const valid =
      typeof fromClass === 'number' &&
      !!fromSection &&
      typeof toClass === 'number' &&
      !!toSection;
    if (!valid) {
      notify('error', 'Please Fill all the Required Fields');
      return;
    }

    const checked = studentList.filter((x) => x.check);
    const insert_list = checked.map((l) => ({
      school_key_id: l.school_id,
      student_id: l.id,
      from_class: fromClass,
      to_class: targetValue,
      from_section: fromSection,
      to_section: toSection,
      ac_year: acYear,
    }));
    const update_list = checked.map((l) => ({
      id: l.id,
      class_studying_id: targetValue,
      class_section: toSection,
      education_medium_id: targetMedium,
      group_code_id: targetGroupId,
      prv_class_std: 2,
    }));

    const fromLabel = classData.find((c) => c.value === fromClass)?.label ?? '';
    const toLabel =
      classDataTarget.find((c) => c.value === targetValue)?.label ?? '';

    confirmDialog({
      header: 'Confirmation',
      icon: 'pi pi-info-circle',
      message: `Are you sure you want to promote ${update_list.length} students from ${fromLabel} - ${fromSection} to ${toLabel} - ${toSection}?`,
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: async () => {
        try {
          const res = await promoteStudents({
            records: { update_list, insert_list },
          }).unwrap();
          if (res.dataStatus) {
            notify('success', 'Student promoted Successfully');
            resetAfterPromote();
          } else {
            notify('error', res.message ?? 'Unable to promote students');
          }
        } catch {
          notify('error', 'Something went wrong. Please try again.');
        }
      },
    });
  };

  const invalid = (touched: boolean, value: unknown): boolean =>
    submitted && touched && !value;

  return (
    <div className="main-body-content student-promotion">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div className="breadcrumbs mb-3">
          <h6 className="url m-0">
            <b>Students</b>
            <span className="ms-2">
              <i className="feather icon-chevron-right" /> Student Promotion
            </span>
          </h6>
        </div>
      </div>

      {/* From / To selection */}
      <div className="card p-4 mb-3">
        <div className="row">
          {/* FROM */}
          <div className="col-12 col-lg-6 border-lg-end">
            <div className="row">
              <div className="col-12 col-md-4">
                <label className="fw-bold mb-1">From</label>
                <Dropdown
                  className="w-100"
                  options={classData}
                  value={fromClass === '' ? null : fromClass}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => handleFromClassChange(e.value)}
                  placeholder="Class"
                />
                {invalid(true, fromClass) && (
                  <small className="text-danger">Class is required</small>
                )}
              </div>
              <div className="col-12 col-md-4 mt-2 mt-md-4">
                <Dropdown
                  className="w-100"
                  options={sectionData}
                  value={fromSection || null}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => handleFromSectionChange(e.value)}
                  placeholder="Section"
                />
                {invalid(true, fromSection) && (
                  <small className="text-danger">Section is required</small>
                )}
              </div>
              <div className="col-12 col-md-4 mt-2 mt-md-4">
                <Button
                  label="Get Student List"
                  className="p-button-primary"
                  onClick={handleGetStudentList}
                />
              </div>
            </div>

            <div className="row mt-3 align-items-center">
              <div className="col-4 col-md-2">
                <label className="fnt-size">Medium</label>
              </div>
              <div className="col-8 col-md-10">
                <InputText
                  className="form-control group-clss"
                  value={fromMediumName}
                  disabled
                />
              </div>
              <div className="col-4 col-md-2 mt-2">
                <label className="fnt-size">Group</label>
              </div>
              <div className="col-8 col-md-10 mt-2">
                <InputText
                  className="form-control group-clss"
                  value={fromGroupName}
                  disabled
                />
              </div>
            </div>
          </div>

          {/* TO */}
          <div className="col-12 col-lg-6 mt-3 mt-lg-0">
            <div className="row">
              <div className="col-12 col-md-6">
                <label className="fw-bold mb-1">To</label>
                <Dropdown
                  className="w-100"
                  options={classDataTarget}
                  value={toClass === '' ? null : toClass}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => handleToClassChange(e.value)}
                  placeholder="Class"
                />
                {invalid(true, toClass) && (
                  <small className="text-danger">Class is required</small>
                )}
              </div>
              <div className="col-12 col-md-6 mt-2 mt-md-4">
                <Dropdown
                  className="w-100"
                  options={sectionDataTarget}
                  value={toSection || null}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => handleToSectionChange(e.value)}
                  placeholder="Section"
                />
                {invalid(true, toSection) && (
                  <small className="text-danger">Section is required</small>
                )}
              </div>
            </div>

            <div className="row mt-3 align-items-center">
              <div className="col-4 col-md-2">
                <label className="fnt-size">Medium</label>
              </div>
              <div className="col-8 col-md-10">
                <InputText
                  className="form-control group-clss"
                  value={toMediumName}
                  disabled
                />
              </div>
              <div className="col-4 col-md-2 mt-2">
                <label className="fnt-size">Group</label>
              </div>
              <div className="col-8 col-md-10 mt-2">
                <InputText
                  className="form-control group-clss"
                  value={toGroupName}
                  disabled
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Student list */}
      <div className="card p-4">
        <div className="mb-2 d-flex justify-content-end">
          <span
            className="p-input-icon-left"
            style={{ width: '100%', maxWidth: 320 }}
          >
            <i className="pi pi-search ps-1" />
            <input
              type="text"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search keyword"
              className="global-search-input ps-4 w-100"
            />
          </span>
        </div>

        <DataTable
          value={studentList}
          sortMode="single"
          size="small"
          stripedRows
          responsiveLayout="scroll"
          emptyMessage="No Records Found"
          globalFilter={globalFilter}
          globalFilterFields={['user_id', 'name']}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
          <Column
            header="S.No"
            body={(_r, { rowIndex }) => rowIndex + 1}
            style={{ width: '70px' }}
          />
          <Column
            style={{ width: '60px' }}
            header={
              <Checkbox
                onChange={handleSelectAll}
                checked={studentAllCheck}
                disabled={studentList.length === 0}
              />
            }
            body={(row: StudentRow) => (
              <Checkbox
                checked={row.check}
                onChange={(e) => handleRowCheck(row.id, !!e.checked)}
              />
            )}
          />
          <Column field="user_id" header="Student EMIS ID" sortable />
          <Column field="name" header="Student Name" sortable />
        </DataTable>

        {promoteView && (
          <div className="row mt-3">
            <div className="col-12 text-end">
              <Button
                label="Promote"
                className="p-button-sm p-button-success"
                onClick={handlePromote}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentPromotion;
